/**
 * Les sous-pages d'un calque dans la toolbox (Remplissage, Police, Disposition…), et les
 * pages du fond de la page (slide) ou de la séquence sélectionnée (couleur, image, vidéo).
 *
 * Le serveur rend chaque sous-page depuis le document sauvegardé : l'url ne porte que le
 * calque (ou la page, la séquence), et le porteur. L'autosave passe avant chaque requête du
 * frame, et la sous-page ouverte se recharge après chaque sauvegarde (`reload`).
 *
 * Les pages du fond se nomment, pour la barre, `background-color`, `background-image`,
 * `background-video`, `background-motion` ;
 * leurs urls viennent du serveur en gabarits (`__id__` : la page).
 */

/** Les sous-pages d'un calque — rendues par le serveur depuis le document. */
const PAGES = ["animations", "appearance", "image", "layout", "variables", "condition"] as const;
const FRAME_ID = "toolbox-content";
const BACKGROUND = "background";
const BACKGROUND_PANELS = ["color", "image", "video", "motion"];
const PANEL_ID = "__id__";

/** Ce dont le fond est le fond : une page de l'éditeur images, ou une séquence vidéo. */
export interface BackgroundTarget {
  kind: "slide" | "sequence"
  id: string
}
/** Ce qu'on demande au <turbo-frame> de la toolbox (FrameElement de Turbo). */
type ToolboxFrame = HTMLElement & { src: string | null; complete: boolean; reload: () => void };

export class FabricToolboxPages {
  private readonly base: { path: string; params: URLSearchParams } | null
  private readonly getPageId: () => string | null | undefined
  private readonly panelUrls: Partial<Record<BackgroundTarget["kind"], string>>

  /**
   * @param baseUrl la base des sous-pages (chemin + porteur du document), posée par le serveur
   * @param getPageId la page du document que le canvas montre (éditeur images) : le fond
   *   s'appelle `bg` sur chaque page, la sous-page a besoin de savoir laquelle
   */
  constructor(
    baseUrl: string | undefined,
    getPageId: () => string | null | undefined = () => null,
    panelUrls: Partial<Record<BackgroundTarget["kind"], string>> = {},
  ) {
    this.panelUrls = panelUrls;
    const [path, query] = (baseUrl || "").split("?");
    this.base = path ? { path: path.replace(/\/$/, ""), params: new URLSearchParams(query || "") } : null;
    this.getPageId = getPageId;
  }

  /** L'url d'une sous-page pour un calque. */
  urlFor(page: string, layerId: string): string | null {
    if (!this.base) return null;
    const params = new URLSearchParams(this.base.params);
    const pageId = this.getPageId();
    if (pageId) params.set("page", pageId);
    const query = params.toString();
    return `${this.base.path}/${encodeURIComponent(layerId)}/${page}${query ? `?${query}` : ""}`;
  }

  /** L'url d'une page du fond (`color` par défaut) : celle de la page, ou de la séquence. */
  backgroundUrl(target: BackgroundTarget, panel = "color"): string | null {
    const template = this.panelUrls[target.kind];
    if (!template || !this.base) return null;
    const query = this.base.params.toString();
    return `${template.replace(PANEL_ID, encodeURIComponent(target.id))}/${panel}${query ? `?${query}` : ""}`;
  }

  /** Les liens du fond sous `root` (`[data-page^=background-]`) visent leur page. */
  linkBackground(root: HTMLElement, target: BackgroundTarget): void {
    root.querySelectorAll<HTMLAnchorElement>(`[data-page^="${BACKGROUND}-"]`).forEach((link) => {
      const url = this.backgroundUrl(target, link.dataset.page!.slice(BACKGROUND.length + 1));
      if (url) link.href = url;
    });
  }

  /** Les liens `[data-page]` sous `root` visent le calque. */
  link(root: HTMLElement, layerId: string | undefined): void {
    if (!layerId) return;
    root.querySelectorAll<HTMLAnchorElement>("[data-page]").forEach((link) => {
      if (this.isBackground(link.dataset.page)) return;
      const url = link.dataset.page && this.urlFor(link.dataset.page, layerId);
      if (url) link.href = url;
    });
  }

  /** La sous-page ouverte suit la sélection : elle ne montre jamais l'objet d'avant. */
  follow(layerId: string | undefined): void {
    const page = this.openPage();
    const frame = this.frame();
    if (layerId && page && !this.isBackground(page) && frame) frame.src = this.urlFor(page, layerId);
  }

  /** Le document vient d'être sauvegardé : la sous-page ouverte se recharge (morph) et montre
   *  ce que le document dit. Un frame encore en chargement (la sauvegarde que son ouverture
   *  attendait) rendra ce document de lui-même. Les pages Image et Vidéo du fond montrent la
   *  bibliothèque, pas le document : elles ne se rechargent pas (elles repartiraient de zéro
   *  à chaque sauvegarde). */
  reload(): void {
    const frame = this.frame();
    const page = this.openPage();
    if (!frame?.complete || !page || [`${BACKGROUND}-image`, `${BACKGROUND}-video`].includes(page)) return;
    frame.reload();
  }

  /** La sous-page que le frame montre, s'il en montre une : celle d'un calque, ou la page
   *  Fond (par son onglet). */
  openPage(): string | null {
    const src = this.frame()?.src;
    if (!src || !this.base) return null;
    try {
      const { pathname } = new URL(src, window.location.origin);
      const panel = this.backgroundPanelOf(pathname);
      if (panel) return `${BACKGROUND}-${panel}`;
      if (!pathname.startsWith(`${this.base.path}/`)) return null;
      return PAGES.find((page) => pathname.endsWith(`/${page}`)) || null;
    } catch {
      return null;
    }
  }

  /** La page du fond qu'une url montre (`color`, `image`, `video`), si c'en est une. */
  private backgroundPanelOf(pathname: string): string | null {
    const panel = pathname.split("/").pop() || "";
    if (!BACKGROUND_PANELS.includes(panel)) return null;
    const prefixes = Object.values(this.panelUrls).map((template) => template!.split(PANEL_ID)[0]);
    return prefixes.some((prefix) => pathname.startsWith(prefix)) ? panel : null;
  }

  /** Une page du fond (`background-<page>`) ? */
  isBackground(page: string | null | undefined): boolean {
    return Boolean(page?.startsWith(`${BACKGROUND}-`));
  }

  private frame(): ToolboxFrame | null {
    return document.getElementById(FRAME_ID) as ToolboxFrame | null;
  }
}
